import { memo, useCallback, useState, useRef } from 'react'
import classNames from 'classnames'
import { useGesture } from '@use-gesture/react'
import { createUseStyles } from 'react-jss'
import RevealFadeIn from '@/components/RevealFadeIn'
import RevealText from '@/components/RevealText'
import ParallaxImg from '@/components/ParallaxImg'
import useMediaQuery from '@/hooks/useMediaQuery'
import { removeParagraph, cutExcerpt } from '@/utils/strings'
import style from './style'

const useStyles = createUseStyles(style)

const Story = ({
  title,
  items,
}) => {
  const [active, setActive] = useState(0)
  const classes = useStyles({ index: items.length > 3 ? active : 0 })
  const $slider = useRef()
  const { match: isMobile } = useMediaQuery('sm')

  /*------------------------------
  Handle Go Prev
  ------------------------------*/
  const goPrev = useCallback(() => setActive(active - 1 < 0 ? items.length - 1 : active - 1), [active])

  /*------------------------------
  Handle Go Next
  ------------------------------*/
  const goNext = useCallback(() => setActive(active + 1 >= items.length ? 0 : active + 1), [active])

  /*------------------------------
  Handle Click Year
  ------------------------------*/
  const handleClickYear = useCallback((i) => setActive(i), [])

  /*------------------------------
  Handle Drag
  ------------------------------*/
  useGesture({
    onDragEnd: ({ direction: [x], axis }) => {
      if (axis === 'x') {
        if (x === -1) goNext()
        if (x === 1) goPrev()
      }
    },
  }, {
    target: $slider,
    eventOptions: { passive: true },
    enabled: isMobile,
  })

  return (
    <div className={classes.root}>
      <h2
        className={classes.title}
        dangerouslySetInnerHTML={{ __html: removeParagraph(title) }}
      />
      <div className={classes.content}>
        <div className={classes.sidebar}>
          <ul>
            {items.map((item, i) => (
              <li key={i.toString()}>
                <button
                  className={classNames({
                    [classes.yearActive]: active === i,
                  })}
                  onClick={() => handleClickYear(i)}
                  label={item.year}
                  dangerouslySetInnerHTML={{ __html: item.year }}
                  aria-label={`go-to-year-${item.year}`}
                />
              </li>
            ))}
          </ul>
        </div>
        <div
          className={classes.slides}
          ref={$slider}
        >
          {items.map((item, i) => (
            <div
              className={classes.slide}
              key={i.toString()}
            >
              <div className={classes.buttons}>
                <button
                  onClick={goPrev}
                  className={classes.prev}
                  aria-label="go-to-prev-slide"
                />
                <button
                  onClick={goNext}
                  className={classes.next}
                  aria-label="go-to-next-slide"
                />
              </div>
              <div className={`${classes.right} ${classes.wrapperImage}`}>
                <RevealFadeIn visible={active === i}>
                  <div className={classes.image}>
                    <ParallaxImg img={item.image.url} />
                  </div>
                </RevealFadeIn>
                <RevealText
                  className={classes.yearLeft}
                  tag="div"
                  value={item.year.slice(0, 2)}
                  stagger={0.3}
                  lineHeight={0.9}
                  enterDelay={0.05}
                  exitDelay={0}
                  visible={active === i}
                  once={false}
                />
                <RevealText
                  className={classes.yearBottom}
                  tag="div"
                  value={item.year.slice(2, 4)}
                  stagger={0.3}
                  lineHeight={0.9}
                  enterDelay={0.05}
                  exitDelay={0}
                  visible={active === i}
                  once={false}
                />
              </div>
              <div className={classes.info}>
                <div className={classes.left}>
                  <RevealText
                    tag="h3"
                    type="lines"
                    value={removeParagraph(isMobile ? `${item.year} - ${item.title}` : item.title)}
                    lineHeight={1.2}
                    enterDelay={0.05}
                    exitDelay={0}
                    visible={active === i}
                    once={false}
                  />
                  <RevealFadeIn
                    enterDelay={0.05}
                    exitDelay={0}
                    visible={active === i}
                  >
                    <p dangerouslySetInnerHTML={{ __html: item.text > 620 ? cutExcerpt(item.text, 620) : item.text }} />
                  </RevealFadeIn>
                </div>
                <div className={classes.right} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default memo(Story)
